import { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { studentsApi } from '../lib/api'
import { Student } from '../types'

export default function Students() {
  const [students, setStudents] = useState<Student[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // 对话框状态
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingStudent, setEditingStudent] = useState<Student | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    grade: '',
    class: '',
    studentId: '',
  })

  const loadStudents = async () => {
    try {
      const data = await studentsApi.getAll()
      setStudents(data)
    } catch (err: any) {
      setError(err.message || '加载学生列表失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStudents()
  }, [])

  const handleOpenDialog = (student?: Student) => {
    if (student) {
      setEditingStudent(student)
      setFormData({
        name: student.name,
        grade: student.grade,
        class: student.class,
        studentId: student.studentId || '',
      })
    } else {
      setEditingStudent(null)
      setFormData({ name: '', grade: '', class: '', studentId: '' })
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingStudent(null)
  }

  const handleSubmit = async () => {
    if (!formData.name || !formData.grade || !formData.class) {
      setError('请填写必填字段')
      return
    }

    try {
      if (editingStudent) {
        await studentsApi.update(editingStudent.id, formData)
      } else {
        await studentsApi.create(formData)
      }
      handleCloseDialog()
      loadStudents()
    } catch (err: any) {
      setError(err.message || '保存失败')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这个学生吗？相关作业数据也会被删除。')) return
    try {
      await studentsApi.delete(id)
      loadStudents()
    } catch (err: any) {
      setError(err.message || '删除失败')
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">学生管理</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          添加学生
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {students.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              暂无学生
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              请先添加学生信息，然后才能上传作业进行分析
            </Typography>
            <Button variant="contained" onClick={() => handleOpenDialog()}>
              添加第一个学生
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>姓名</TableCell>
                <TableCell>年级</TableCell>
                <TableCell>班级</TableCell>
                <TableCell>学号</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map(student => (
                <TableRow key={student.id} hover>
                  <TableCell>{student.name}</TableCell>
                  <TableCell>{student.grade}</TableCell>
                  <TableCell>{student.class}</TableCell>
                  <TableCell>{student.studentId || '-'}</TableCell>
                  <TableCell>{new Date(student.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(student)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(student.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 添加/编辑学生 Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingStudent ? '编辑学生' : '添加学生'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="姓名"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              required
              autoFocus
            />
            <TextField
              label="年级"
              value={formData.grade}
              onChange={e => setFormData({ ...formData, grade: e.target.value })}
              required
              placeholder="如：三年级"
            />
            <TextField
              label="班级"
              value={formData.class}
              onChange={e => setFormData({ ...formData, class: e.target.value })}
              required
              placeholder="如：2班"
            />
            <TextField
              label="学号（可选）"
              value={formData.studentId}
              onChange={e => setFormData({ ...formData, studentId: e.target.value })}
              placeholder="如：20240102"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.grade || !formData.class}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
